// M07 指數、指數函數與對數函數 - Topics 11 to 20
// 涵蓋：對數不等式、底數小於1處理、指數方程、變數代換、因式分解、換底公式、對數轉換、對數方程、聯立方程、指數增長

export const M07_TOPICS_11_TO_20 = [
  {
    id: 'M07_11',
    name: '對數不等式',
    description: '解決涉及對數的不等式問題',
    difficulty: 'advanced'
  },
  {
    id: 'M07_12',
    name: '底數小於1處理',
    description: '處理底數在0與1之間的指數和對數',
    difficulty: 'advanced'
  },
  {
    id: 'M07_13',
    name: '指數方程',
    description: '解決複雜的指數方程',
    difficulty: 'intermediate'
  },
  {
    id: 'M07_14',
    name: '變數代換',
    description: '使用變數代換簡化複雜問題',
    difficulty: 'advanced'
  },
  {
    id: 'M07_15',
    name: '因式分解',
    description: '在指數和對數問題中應用因式分解',
    difficulty: 'advanced'
  },
  {
    id: 'M07_16',
    name: '換底公式',
    description: '掌握和應用換底公式',
    difficulty: 'intermediate'
  },
  {
    id: 'M07_17',
    name: '對數轉換',
    description: '不同底數對數之間的轉換',
    difficulty: 'intermediate'
  },
  {
    id: 'M07_18',
    name: '對數方程',
    description: '解決各種對數方程',
    difficulty: 'intermediate'
  },
  {
    id: 'M07_19',
    name: '聯立方程',
    description: '解決指數對數的聯立方程組',
    difficulty: 'advanced'
  },
  {
    id: 'M07_20',
    name: '指數增長',
    description: '指數增長模型的應用',
    difficulty: 'intermediate'
  }
];

export const M07_PROBLEMS_11_TO_20 = [
  // Topic 11: 對數不等式 (5 questions)
  {
    id: 'M07_11_01',
    topicId: 'M07_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '解對數不等式（底數大於1）：',
    parts: {
      a: {
        question: '解不等式 $\\log_2 x > 3$',
        solution: {
          steps: [
            '由於底數 2 > 1，對數函數單調遞增',
            '轉換為指數形式：$x > 2^3 = 8$',
            '考慮定義域：$x > 0$，所以解為 $x > 8$'
          ],
          answer: '$x > 8$'
        },
        hint: '底數大於1時，對數函數單調遞增'
      },
      b: {
        question: '解不等式 $\\log_3(x-1) < 2$',
        solution: {
          steps: [
            '由於底數 3 > 1，對數函數單調遞增',
            '轉換：$x - 1 < 3^2 = 9$，得 $x < 10$',
            '考慮定義域：$x - 1 > 0$，即 $x > 1$，所以解為 $1 < x < 10$'
          ],
          answer: '$1 < x < 10$'
        },
        hint: '注意對數的定義域限制'
      },
      c: {
        question: '解不等式 $\\log_5(2x+1) \\geq 1$',
        solution: {
          steps: [
            '由於底數 5 > 1，轉換：$2x + 1 \\geq 5^1 = 5$',
            '解不等式：$2x \\geq 4$，得 $x \\geq 2$',
            '檢查定義域：$2x + 1 > 0$，即 $x > -\\frac{1}{2}$，所以解為 $x \\geq 2$'
          ],
          answer: '$x \\geq 2$'
        },
        hint: '先轉換不等式，再檢查定義域'
      }
    }
  },
  {
    id: 'M07_11_02',
    topicId: 'M07_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '解對數不等式（底數在0與1之間）：',
    parts: {
      a: {
        question: '解不等式 $\\log_{0.5} x > 2$',
        solution: {
          steps: [
            '由於底數 0.5 < 1，對數函數單調遞減',
            '不等號方向改變：$x < (0.5)^2 = 0.25$',
            '考慮定義域：$x > 0$，所以解為 $0 < x < 0.25$'
          ],
          answer: '$0 < x < 0.25$'
        },
        hint: '底數小於1時，對數函數單調遞減，不等號要變向'
      },
      b: {
        question: '解不等式 $\\log_{\\frac{1}{3}}(x+2) < 1$',
        solution: {
          steps: [
            '由於底數 $\\frac{1}{3} < 1$，不等號變向：$x + 2 > (\\frac{1}{3})^1 = \\frac{1}{3}$',
            '解得：$x > \\frac{1}{3} - 2 = -\\frac{5}{3}$',
            '檢查定義域：$x + 2 > 0$，即 $x > -2$，所以解為 $x > -\\frac{5}{3}$'
          ],
          answer: '$x > -\\frac{5}{3}$'
        },
        hint: '底數小於1時要變向，並檢查定義域'
      },
      c: {
        question: '解不等式 $\\log_{0.2}(3-x) \\geq -1$',
        solution: {
          steps: [
            '由於底數 0.2 < 1，變向：$3 - x \\leq (0.2)^{-1} = 5$',
            '解得：$-x \\leq 2$，即 $x \\geq -2$',
            '檢查定義域：$3 - x > 0$，即 $x < 3$，所以解為 $-2 \\leq x < 3$'
          ],
          answer: '$-2 \\leq x < 3$'
        },
        hint: '注意負指數的計算'
      }
    }
  },
  {
    id: 'M07_11_03',
    topicId: 'M07_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '解複合對數不等式：',
    parts: {
      a: {
        question: '解不等式 $\\log_2 x + \\log_2(x-3) > 2$',
        solution: {
          steps: [
            '利用對數加法性質：$\\log_2[x(x-3)] > 2$',
            '轉換：$x(x-3) > 2^2 = 4$，即 $x^2 - 3x > 4$',
            '重排：$x^2 - 3x - 4 > 0$，因式分解：$(x-4)(x+1) > 0$'
          ],
          answer: '$x > 4$（考慮定義域 $x > 3$）'
        },
        hint: '先用對數性質合併，再考慮定義域'
      },
      b: {
        question: '解不等式 $\\log_3(x+1) - \\log_3(x-1) < 1$',
        solution: {
          steps: [
            '利用對數減法性質：$\\log_3 \\frac{x+1}{x-1} < 1$',
            '轉換：$\\frac{x+1}{x-1} < 3^1 = 3$',
            '解分式不等式：$\\frac{x+1-3(x-1)}{x-1} < 0$'
          ],
          answer: '$1 < x < 2$'
        },
        hint: '化簡分式不等式時要考慮分母的符號'
      },
      c: {
        question: '解不等式 $|\\log_2 x| < 1$',
        solution: {
          steps: [
            '絕對值不等式：$-1 < \\log_2 x < 1$',
            '轉換左邊：$\\log_2 x > -1$，即 $x > 2^{-1} = \\frac{1}{2}$',
            '轉換右邊：$\\log_2 x < 1$，即 $x < 2^1 = 2$'
          ],
          answer: '$\\frac{1}{2} < x < 2$'
        },
        hint: '絕對值不等式要分兩個方向處理'
      }
    }
  },
  {
    id: 'M07_11_04',
    topicId: 'M07_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '解指數不等式：',
    parts: {
      a: {
        question: '解不等式 $2^x > 8$',
        solution: {
          steps: [
            '將 8 表示為 2 的指數：$8 = 2^3$',
            '不等式變為：$2^x > 2^3$',
            '由於底數 2 > 1，指數函數單調遞增：$x > 3$'
          ],
          answer: '$x > 3$'
        },
        hint: '將兩邊轉換為相同底數'
      },
      b: {
        question: '解不等式 $(\\frac{1}{2})^x < 4$',
        solution: {
          steps: [
            '將底數和右邊都表示為 2 的指數：$(\\frac{1}{2})^x = (2^{-1})^x = 2^{-x}$，$4 = 2^2$',
            '不等式變為：$2^{-x} < 2^2$',
            '由於底數 2 > 1，得：$-x < 2$，即 $x > -2$'
          ],
          answer: '$x > -2$'
        },
        hint: '注意負指數的處理'
      },
      c: {
        question: '解不等式 $3^{2x-1} \\geq 9$',
        solution: {
          steps: [
            '將 9 表示為 3 的指數：$9 = 3^2$',
            '不等式變為：$3^{2x-1} \\geq 3^2$',
            '由於底數 3 > 1，得：$2x - 1 \\geq 2$，即 $2x \\geq 3$，$x \\geq \\frac{3}{2}$'
          ],
          answer: '$x \\geq \\frac{3}{2}$'
        },
        hint: '處理指數中的線性表達式'
      }
    }
  },
  {
    id: 'M07_11_05',
    topicId: 'M07_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '綜合不等式問題：',
    parts: {
      a: {
        question: '解不等式組 $\\begin{cases} \\log_2 x > 1 \\\\ \\log_2 x < 3 \\end{cases}$',
        solution: {
          steps: [
            '第一個不等式：$\\log_2 x > 1$，得 $x > 2$',
            '第二個不等式：$\\log_2 x < 3$，得 $x < 8$',
            '取交集：$2 < x < 8$'
          ],
          answer: '$2 < x < 8$'
        },
        hint: '分別解每個不等式，再求交集'
      },
      b: {
        question: '解不等式 $\\log_2(x^2 - 1) > 3$',
        solution: {
          steps: [
            '轉換：$x^2 - 1 > 2^3 = 8$，得 $x^2 > 9$',
            '解得：$|x| > 3$，即 $x > 3$ 或 $x < -3$',
            '檢查定義域：$x^2 - 1 > 0$，即 $x > 1$ 或 $x < -1$'
          ],
          answer: '$x > 3$ 或 $x < -3$'
        },
        hint: '先解不等式，再檢查定義域'
      },
      c: {
        question: '解不等式 $2^x + 2^{-x} > 2.5$',
        solution: {
          steps: [
            '設 $y = 2^x > 0$，則 $2^{-x} = \\frac{1}{y}$',
            '不等式變為：$y + \\frac{1}{y} > 2.5$',
            '乘以 $y$：$y^2 + 1 > 2.5y$，即 $y^2 - 2.5y + 1 > 0$'
          ],
          answer: '$x < 0$ 或 $x > \\log_2 2 = 1$'
        },
        hint: '使用代換法簡化問題'
      }
    }
  },

  // Topic 12: 底數小於1處理 (5 questions)
  {
    id: 'M07_12_01',
    topicId: 'M07_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '處理底數在0與1之間的指數函數：',
    parts: {
      a: {
        question: '計算 $(\\frac{1}{2})^3$',
        solution: {
          steps: [
            '直接計算：$(\\frac{1}{2})^3 = \\frac{1^3}{2^3} = \\frac{1}{8}$',
            '或轉換為負指數：$(\\frac{1}{2})^3 = (2^{-1})^3 = 2^{-3} = \\frac{1}{8}$',
            '驗證：$\\frac{1}{2} \\times \\frac{1}{2} \\times \\frac{1}{2} = \\frac{1}{8}$ ✓'
          ],
          answer: '$(\\frac{1}{2})^3 = \\frac{1}{8}$'
        },
        hint: '分數的指數等於分子指數除以分母指數'
      },
      b: {
        question: '化簡 $(0.3)^{-2}$',
        solution: {
          steps: [
            '負指數的定義：$(0.3)^{-2} = \\frac{1}{(0.3)^2}$',
            '計算：$(0.3)^2 = 0.09$',
            '結果：$(0.3)^{-2} = \\frac{1}{0.09} = \\frac{100}{9}$'
          ],
          answer: '$(0.3)^{-2} = \\frac{100}{9}$'
        },
        hint: '負指數表示倒數'
      },
      c: {
        question: '比較 $(\\frac{2}{3})^2$ 和 $(\\frac{2}{3})^3$ 的大小',
        solution: {
          steps: [
            '計算：$(\\frac{2}{3})^2 = \\frac{4}{9}$，$(\\frac{2}{3})^3 = \\frac{8}{27}$',
            '比較：$\\frac{4}{9} = \\frac{12}{27}$，$\\frac{8}{27} = \\frac{8}{27}$',
            '由於 $\\frac{12}{27} > \\frac{8}{27}$，所以 $(\\frac{2}{3})^2 > (\\frac{2}{3})^3$'
          ],
          answer: '$(\\frac{2}{3})^2 > (\\frac{2}{3})^3$'
        },
        hint: '當底數在0與1之間時，指數越大，值越小'
      }
    }
  },
  {
    id: 'M07_12_02',
    topicId: 'M07_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '處理底數小於1的對數：',
    parts: {
      a: {
        question: '計算 $\\log_{0.5} 0.25$',
        solution: {
          steps: [
            '將底數和真數都表示為 2 的負指數：$0.5 = 2^{-1}$，$0.25 = (\\frac{1}{2})^2 = 2^{-2}$',
            '使用換底公式：$\\log_{0.5} 0.25 = \\frac{\\log_2 0.25}{\\log_2 0.5} = \\frac{-2}{-1} = 2$',
            '驗證：$(0.5)^2 = 0.25$ ✓'
          ],
          answer: '$\\log_{0.5} 0.25 = 2$'
        },
        hint: '使用換底公式或轉換為相同的基本底數'
      },
      b: {
        question: '解方程 $\\log_{0.2} x = -1$',
        solution: {
          steps: [
            '轉換為指數形式：$x = (0.2)^{-1}$',
            '計算：$(0.2)^{-1} = \\frac{1}{0.2} = 5$',
            '驗證：$\\log_{0.2} 5 = -1$ ✓'
          ],
          answer: '$x = 5$'
        },
        hint: '負指數表示倒數'
      },
      c: {
        question: '化簡 $\\log_{\\frac{1}{3}} 9$',
        solution: {
          steps: [
            '將底數和真數都表示為 3 的指數：$\\frac{1}{3} = 3^{-1}$，$9 = 3^2$',
            '設 $\\log_{\\frac{1}{3}} 9 = x$，則 $(3^{-1})^x = 3^2$',
            '得：$3^{-x} = 3^2$，所以 $-x = 2$，$x = -2$'
          ],
          answer: '$\\log_{\\frac{1}{3}} 9 = -2$'
        },
        hint: '底數小於1時，對數值通常為負'
      }
    }
  },
  {
    id: 'M07_12_03',
    topicId: 'M07_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '底數小於1的函數性質：',
    parts: {
      a: {
        question: '判斷函數 $f(x) = (\\frac{1}{2})^x$ 的單調性',
        solution: {
          steps: [
            '當底數 $0 < a < 1$ 時，指數函數 $f(x) = a^x$ 單調遞減',
            '驗證：當 $x_1 < x_2$ 時，由於 $\\frac{1}{2} < 1$，有 $(\\frac{1}{2})^{x_1} > (\\frac{1}{2})^{x_2}$',
            '例如：$(\\frac{1}{2})^1 = 0.5 > (\\frac{1}{2})^2 = 0.25$'
          ],
          answer: '$f(x) = (\\frac{1}{2})^x$ 單調遞減'
        },
        hint: '底數小於1的指數函數單調遞減'
      },
      b: {
        question: '判斷函數 $g(x) = \\log_{0.3} x$ 的單調性',
        solution: {
          steps: [
            '當底數 $0 < a < 1$ 時，對數函數 $g(x) = \\log_a x$ 單調遞減',
            '驗證：當 $x_1 < x_2$ 時，有 $\\log_{0.3} x_1 > \\log_{0.3} x_2$',
            '例如：$\\log_{0.3} 1 = 0 > \\log_{0.3} 3 < 0$'
          ],
          answer: '$g(x) = \\log_{0.3} x$ 單調遞減'
        },
        hint: '底數小於1的對數函數單調遞減'
      },
      c: {
        question: '求函數 $h(x) = (0.4)^x$ 的值域',
        solution: {
          steps: [
            '指數函數的定義域為 $\\mathbb{R}$',
            '由於底數 $0 < 0.4 < 1$，函數單調遞減',
            '當 $x \\to -\\infty$ 時，$h(x) \\to +\\infty$；當 $x \\to +\\infty$ 時，$h(x) \\to 0^+$'
          ],
          answer: '值域為 $(0, +\\infty)$'
        },
        hint: '指數函數的值域總是正實數'
      }
    }
  },
  {
    id: 'M07_12_04',
    topicId: 'M07_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '底數轉換技巧：',
    parts: {
      a: {
        question: '將 $(\\frac{2}{3})^x = \\frac{9}{4}$ 轉換為同底數形式',
        solution: {
          steps: [
            '注意到 $\\frac{9}{4} = (\\frac{3}{2})^2$',
            '而 $\\frac{2}{3} = (\\frac{3}{2})^{-1}$',
            '所以方程變為：$[(\\frac{3}{2})^{-1}]^x = (\\frac{3}{2})^2$，即 $(\\frac{3}{2})^{-x} = (\\frac{3}{2})^2$'
          ],
          answer: '$-x = 2$，所以 $x = -2$'
        },
        hint: '尋找倒數關係進行底數統一'
      },
      b: {
        question: '化簡 $\\log_{0.25} 2$',
        solution: {
          steps: [
            '將底數表示為 2 的指數：$0.25 = \\frac{1}{4} = \\frac{1}{2^2} = 2^{-2}$',
            '使用換底公式：$\\log_{0.25} 2 = \\frac{\\log_2 2}{\\log_2 0.25} = \\frac{1}{-2} = -\\frac{1}{2}$',
            '驗證：$(0.25)^{-\\frac{1}{2}} = (2^{-2})^{-\\frac{1}{2}} = 2^1 = 2$ ✓'
          ],
          answer: '$\\log_{0.25} 2 = -\\frac{1}{2}$'
        },
        hint: '將底數轉換為基本底數的指數形式'
      },
      c: {
        question: '解方程 $(0.1)^x = 1000$',
        solution: {
          steps: [
            '將兩邊都表示為 10 的指數：$0.1 = 10^{-1}$，$1000 = 10^3$',
            '方程變為：$(10^{-1})^x = 10^3$，即 $10^{-x} = 10^3$',
            '由於底數相同：$-x = 3$，所以 $x = -3$'
          ],
          answer: '$x = -3$'
        },
        hint: '統一底數後比較指數'
      }
    }
  },
  {
    id: 'M07_12_05',
    topicId: 'M07_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '綜合應用底數小於1的問題：',
    parts: {
      a: {
        question: '比較 $\\log_{0.5} 3$ 和 $\\log_{0.5} 5$ 的大小',
        solution: {
          steps: [
            '由於底數 $0.5 < 1$，對數函數 $\\log_{0.5} x$ 單調遞減',
            '因為 $3 < 5$，所以 $\\log_{0.5} 3 > \\log_{0.5} 5$',
            '驗證：兩個對數都是負數，且 $|\\log_{0.5} 3| < |\\log_{0.5} 5|$'
          ],
          answer: '$\\log_{0.5} 3 > \\log_{0.5} 5$'
        },
        hint: '底數小於1時，對數函數單調遞減'
      },
      b: {
        question: '求 $(\\frac{1}{3})^x + (\\frac{1}{3})^{-x} = \\frac{10}{3}$ 中 $x$ 的值',
        solution: {
          steps: [
            '設 $y = (\\frac{1}{3})^x > 0$，則 $(\\frac{1}{3})^{-x} = \\frac{1}{y}$',
            '方程變為：$y + \\frac{1}{y} = \\frac{10}{3}$',
            '乘以 $y$：$y^2 + 1 = \\frac{10y}{3}$，即 $3y^2 - 10y + 3 = 0$'
          ],
          answer: '$y = 3$ 或 $y = \\frac{1}{3}$，對應 $x = -1$ 或 $x = 1$'
        },
        hint: '使用代換法將問題轉化為二次方程'
      },
      c: {
        question: '解不等式 $(0.2)^{x-1} > (0.2)^{2x+1}$',
        solution: {
          steps: [
            '由於底數 $0.2 < 1$，指數函數單調遞減',
            '不等號變向：$x - 1 < 2x + 1$',
            '解得：$-1 - 1 < 2x - x$，即 $-2 < x$，所以 $x > -2$'
          ],
          answer: '$x > -2$'
        },
        hint: '底數小於1時，指數不等式要變向'
      }
    }
  }

  // Continue with remaining topics 13-20...
  // Due to length constraints, I'll add the remaining topics in subsequent files
];

export default M07_TOPICS_11_TO_20; 